<template>
<div class="app-shell">
  <div class="stepper-bar">
    <div
      v-for="(step, i) in items"
      :key="step.to"
      class="stepper-item"
      :class="{ active: currentIndex === i, done: currentIndex > i }"
    >
      <div class="stepper-dot">
        <i v-if="currentIndex > i" class="pi pi-check"></i>
        <span v-else>{{ i + 1 }}</span>
      </div>
      <span class="stepper-label">{{ step.label }}</span>
    </div>
    <div class="stepper-line">
      <div class="stepper-progress" :style="{ width: progressWidth }"></div>
    </div>
  </div>

  <div class="versionTag">v2026.04.04</div>
  <div class="step-content">
    <router-view v-slot="{ Component }" @previous-page="previousPage($event)" @next-page="nextPage($event)" @go-home="goHome()" @go-confirm="goConfirm()">
      <keep-alive>
        <component :is="Component" />
      </keep-alive>
    </router-view>
  </div>
</div>
</template>

<script>
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';

export default({
  setup() {
    const router = useRouter();
    const route = useRoute();

    const items = ref([
      { label: 'Contact', to: '/home' },
      { label: 'Cheveux', to: '/hair' },
      { label: 'Services', to: '/services' },
      { label: 'Rendez-vous', to: '/rdv' }
    ]);

    const currentIndex = computed(() => {
      const idx = items.value.findIndex(item => item.to === route.path);
      return idx >= 0 ? idx : (route.path === '/merci' ? items.value.length : 0);
    });

    const progressWidth = computed(() => {
      if (currentIndex.value >= items.value.length) return '100%';
      return `${(currentIndex.value / (items.value.length - 1)) * 100}%`;
    });

    const nextPage = (event) => {
      router.push(items.value[event.pageIndex + 1].to);
    };

    const previousPage = (event) => {
      router.push(items.value[event.pageIndex - 1].to);
    };

    const goHome = () => {
      router.push(items.value[0].to);
    };

    const goConfirm = () => {
      router.push('/merci');
    };

    return { items, currentIndex, progressWidth, nextPage, previousPage, goHome, goConfirm };
  }
});
</script>

<style>
*,
*::before,
*::after {
  box-sizing: border-box;
}

body {
  margin: 0;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8edf7 100%);
  min-height: 100vh;
}

#app {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #1e293b;
}

.app-shell {
  max-width: 640px;
  margin: 0 auto;
  padding: 1.25rem 1rem 2rem;
}

/* ── Stepper ── */
.stepper-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  padding: 0 0.5rem;
  margin-bottom: 1.5rem;
}

.stepper-line {
  position: absolute;
  top: 16px;
  left: 32px;
  right: 32px;
  height: 3px;
  background: #cbd5e1;
  border-radius: 2px;
  z-index: 0;
}

.stepper-progress {
  height: 100%;
  background: #3b82f6;
  border-radius: 2px;
  transition: width 0.4s ease;
}

.stepper-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  z-index: 1;
  min-width: 54px;
}

.stepper-dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  background: #e2e8f0;
  color: #94a3b8;
  border: 2px solid #cbd5e1;
  transition: all 0.3s ease;
}

.stepper-item.active .stepper-dot {
  background: #3b82f6;
  color: #fff;
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
}

.stepper-item.done .stepper-dot {
  background: #10b981;
  color: #fff;
  border-color: #10b981;
}

.stepper-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
  text-align: center;
  letter-spacing: 0.01em;
}

.stepper-item.active .stepper-label {
  color: #3b82f6;
}

.stepper-item.done .stepper-label {
  color: #10b981;
}

/* ── Step content ── */
.step-content {
  animation: fadeUp 0.3s ease;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── Shared button styling ── */
.hw-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.hw-btn-primary {
  background: #3b82f6;
  color: #fff;
}

.hw-btn-primary:hover {
  background: #2563eb;
}

.hw-btn-primary:active {
  transform: scale(0.97);
}

.hw-btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.hw-btn-secondary:hover {
  background: #e2e8f0;
}

.hw-btn-success {
  background: #10b981;
  color: #fff;
}

.hw-btn-success:hover {
  background: #059669;
}

.hw-btn-success:active {
  transform: scale(0.97);
}

/* ── Shared card ── */
.hw-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* ── Section title ── */
.hw-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.25rem 0;
}

.hw-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 1.25rem 0;
}

/* ── Actions bar ── */
.hw-actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-bottom: 2rem;
}

/* ── Info fieldset ── */
.p-fieldset {
  border-radius: 12px !important;
  border-color: #e2e8f0 !important;
  margin-top: 1rem;
}

@media (max-width: 480px) {
  .app-shell {
    padding: 1rem 0.75rem 2rem;
  }

  .stepper-label {
    font-size: 0.62rem;
  }

  .stepper-dot {
    width: 28px;
    height: 28px;
    font-size: 0.72rem;
  }

  .stepper-line {
    top: 14px;
    left: 24px;
    right: 24px;
  }

  .hw-card {
    padding: 1.25rem 1rem;
    border-radius: 14px;
  }

  .hw-title {
    font-size: 1.2rem;
  }
}

.versionTag {
  position: fixed;
  bottom: 4px;
  right: 6px;
  font-size: 0.65rem;
  color: #b0b8c8;
  pointer-events: none;
  z-index: 0;
}
</style>
